import { Box, CircularProgress, DialogContent, Divider, styled, Typography, useTheme } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetNewsArticle, useInfiniteNewsFeed } from 'src/api/news'
import { CenteredSpinner } from 'src/components/Spinner'
import { Chip } from 'src/components/common/Chip'
import { Flex } from 'src/styles/flexComponent'
import { MUIThemeColors } from 'src/mui-theme'
import { extractPlainText, formatRelativeTime, getThemeColor } from 'src/utils'
import { ShowDialogProps } from 'src/show-dialog'
import { getNewsTypeLabels, StyledChip } from 'src/components/news/NewsFeed'
import { decode } from 'html-entities'
import { RichTextViewer } from 'src/components/common/RichTextViewer'
interface NewsDetailDialogProps extends ShowDialogProps {
  customerId: EntityId
  articleId: number
}

function formatAuthorName(firstName: string | null, lastName: string | null): string {
  const parts = [firstName, lastName].filter(Boolean)
  return parts.join(' ') || 'System'
}

const NewsDetailDialog: React.FC<NewsDetailDialogProps> = ({ customerId, articleId }) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['common', 'news'])
  const newsTypeLabels = getNewsTypeLabels(translate)
  const [selectedArticleId, setSelectedArticleId] = useState<number>(articleId)
  const { data: articles, isLoading: feedLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteNewsFeed(customerId)
  const { data: article, isLoading: articleLoading } = useGetNewsArticle(customerId, selectedArticleId)
  const listPanelRef = useRef<HTMLDivElement | null>(null)
  const isFetchingRef = useRef(false)

  useEffect(() => {
    isFetchingRef.current = isFetchingNextPage
  }, [isFetchingNextPage])

  useEffect(() => {
    const container = listPanelRef.current
    if (!container) return

    const handleScroll = () => {
      if (isFetchingRef.current || !hasNextPage) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

      if (scrollPercentage >= 0.85) {
        isFetchingRef.current = true
        fetchNextPage().finally(() => {
          setTimeout(() => {
            isFetchingRef.current = false
          }, 300)
        })
      }
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [hasNextPage, fetchNextPage])

  return (
    <>
      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', height: '70vh' }}>
        {/* Left panel – article list */}
        <ArticleListPanel ref={listPanelRef}>
          {feedLoading ? (
            <CenteredSpinner />
          ) : (
            <>
              {articles.map(a => (
                <ArticleListItem
                  key={a.id}
                  selected={a.id === selectedArticleId}
                  onClick={() => setSelectedArticleId(a.id)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Flex.Row sx={{ alignItems: 'center', mb: 0.25, gap: 0.5 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {a.title || 'Untitled'}
                      </Typography>
                      <NewsListChip variant="filled" label={newsTypeLabels[a.newsType]} bgColor={MUIThemeColors.primaryMain} />
                      {a.isImportant && (
                        <NewsListChip variant="filled" label={`${translate('is-important', { ns: 'news' })}`} bgColor={MUIThemeColors.errorMain} />
                      )}
                    </Flex.Row>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {extractPlainText(a.content)}
                    </Typography>
                  </Box>
                </ArticleListItem>
              ))}
              {isFetchingNextPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </>
          )}
        </ArticleListPanel>

        <Divider orientation="vertical" flexItem />

        {/* Right panel – article detail */}
        <ArticleDetailPanel>
          {articleLoading ? (
            <CenteredSpinner />
          ) : article ? (
            <Box>
              <Flex.Row sx={{ alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {article?.title}
                </Typography>
                <StyledChip
                  variant="filled"
                  label={newsTypeLabels[article.newsType]}
                  bgColor={MUIThemeColors.primaryMain}
                  sx={{ ml: 1 }}
                />
                {article.isImportant && (
                  <StyledChip
                    variant="filled"
                    label={translate('is-important', { ns: 'news' })}
                    bgColor={MUIThemeColors.errorMain}
                    sx={{ ml: 0.5 }}
                  />
                )}
              </Flex.Row>

              <RichTextViewer html={decode(article.content ?? '')} maxHeight={300} />

              {article.files && article.files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {article.files
                    .filter(f => f.contentType.startsWith('image/'))
                    .map(file => (
                      <Box key={file.id} sx={{ mb: 1 }}>
                        <img
                          src={file.url}
                          alt={file.filename}
                          style={{ maxWidth: '100%', borderRadius: 8, maxHeight: '220px' }}
                        />
                      </Box>
                    ))}
                  {article.files
                    .filter(f => !f.contentType.startsWith('image/'))
                    .map(file => (
                      <Box key={file.id} sx={{ mb: 0.5 }}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                          {file.filename}
                        </a>
                      </Box>
                    ))}
                </Box>
              )}

              {article.newsArticleLocalityGroups && article.newsArticleLocalityGroups.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {translate('locality-groups', { ns: 'news' })}:
                  </Typography>
                  <Flex.Row sx={{ gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {article.newsArticleLocalityGroups.map(g => (
                      <Chip key={g.localityGroupId} label={g.localityGroupName} size="small" />
                    ))}
                  </Flex.Row>
                </Box>
              )}

              {article.newsArticleLocalities && article.newsArticleLocalities.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {translate('localities', { ns: 'common' })}:
                  </Typography>
                  <Flex.Row sx={{ gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {article.newsArticleLocalities.map(l => (
                      <Chip key={l.localityId} label={l.localityName} size="small" />
                    ))}
                  </Flex.Row>
                </Box>
              )}

              {article.newsArticleServiceStations && article.newsArticleServiceStations.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {translate('service-stations', { ns: 'common' })}:
                  </Typography>
                  <Flex.Row sx={{ gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {article.newsArticleServiceStations.map(s => (
                      <Chip key={s.serviceStationId} label={s.serviceStationName} size="small" />
                    ))}
                  </Flex.Row>
                </Box>
              )}

              {article.newsArticleStorages && article.newsArticleStorages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {translate('storages', { ns: 'common' })}:
                  </Typography>
                  <Flex.Row sx={{ gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {article.newsArticleStorages.map(s => (
                      <Chip key={s.storageId} label={s.storageName} size="small" />
                    ))}
                  </Flex.Row>
                </Box>
              )}

              <Flex.Row sx={{ mt: 1.5, alignItems: 'center' }}>
                <Typography variant="caption" color={getThemeColor(theme, MUIThemeColors.primaryMain)}>
                  {formatAuthorName(article.createdByUserFirstName, article.createdByUserLastName)} |{' '}
                  {formatRelativeTime(article.createdAt.toString())}
                </Typography>
              </Flex.Row>
            </Box>
          ) : null}
        </ArticleDetailPanel>
      </DialogContent>
    </>
  )
}

export default NewsDetailDialog

// Styled components
const ArticleListPanel = styled(Box)`
  width: 280px;
  min-width: 280px;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ArticleDetailPanel = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
`

const ArticleListItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected?: boolean }>(
  ({ theme, selected }) => `
  display: flex;
  flex-direction: row;
  padding: 8px;
  border-radius: 5px;
  border: 1px solid ${selected ? theme.palette.primary.main : theme.palette.secondaryLight[theme.palette.mode]};
  background-color: ${selected ? theme.palette.primary.main + '12' : 'transparent'};
  cursor: pointer;
  &:hover {
    background-color: ${selected ? theme.palette.primary.main + '1a' : theme.palette.secondaryLight[theme.palette.mode] + '8a'};
  }
`,
)

const NewsListChip = styled(Chip) <{ bgColor: MUIThemeColors }>`
  height: 13px;
  font-size: 8px;
  flex-shrink: 0;
  background-color: ${props => getThemeColor(props.theme, props.bgColor)};
  color: ${props => getThemeColor(props.theme, MUIThemeColors.white)};
`
